<script setup lang="ts">
import {computed, nextTick, onBeforeUnmount, ref, set, watch} from "vue"
import {useIntervalFn} from "@vueuse/core"
import ListBrowserToolbar from "~components/admin/ListBrowserToolbar.vue"
import ListBrowserPagination from "~components/admin/ListBrowserPagination.vue"
import ExpandableJsonDetail from "~components/admin/ExpandableJsonDetail.vue"
import {useListBrowser} from "@/composables/useListBrowser"
import {formatDateTime, globParam, setQueryParam} from "@/common/utils/admin"

// ── Types ──────────────────────────────────────────────────────────────────

interface EventEntry {
  id: string
  created: string
  node: string | null
  user_id: number | null
  user_name: string | null
  user_email: string | null
  request_id: string | null
  name: string
  data: Record<string, unknown> | null
  _showDetails?: boolean
}

// ── Filters ────────────────────────────────────────────────────────────────

const nameFilter = ref("")
const userIdFilter = ref("")
const requestIdFilter = ref("")

// ── Normal mode (useListBrowser) ───────────────────────────────────────────

const {
  dateFrom, dateTo, currentPage, perPage, sortBy, sortDesc,
  totalRows, rows, lastPage, doSearch, onSortChanged,
  setDateRange, dateRangeActive,
} = useListBrowser({
  serviceUrl: "/service/objects/event_log_entry",
  defaultSortColumn: "created",
  dateColumn: "created",
  buildFilterParams(params) {
    if (nameFilter.value.trim()) setQueryParam(params, "name_glob", globParam(nameFilter.value.trim()))
    if (userIdFilter.value.trim()) setQueryParam(params, "user_id_eq", userIdFilter.value.trim())
    if (requestIdFilter.value.trim()) setQueryParam(params, "request_id_glob", globParam(requestIdFilter.value.trim()))
  },
  readUrlParams(urlParams) {
    if (urlParams.has("name_glob")) nameFilter.value = urlParams.get("name_glob")!.replace(/\*$/, "")
    if (urlParams.has("user_id_eq")) userIdFilter.value = urlParams.get("user_id_eq")!
    if (urlParams.has("request_id_glob")) requestIdFilter.value = urlParams.get("request_id_glob")!.replace(/\*$/, "")
  },
})

// ── Tail mode ──────────────────────────────────────────────────────────────

const tailMode = ref(false)
const tailPaused = ref(false)
const tailRows = ref<EventEntry[]>([])
const tailLastCreated = ref<string | null>(null)

// ── Computed ───────────────────────────────────────────────────────────────

const fields = [
  {key: "created", label: "Date", sortable: true, thStyle: {maxWidth: "10em"}},
  {key: "name", label: "Event", sortable: true, thStyle: {maxWidth: "20em"}},
  {key: "env", label: "Env", sortable: false, thStyle: {maxWidth: "5em"}},
  {key: "user_name", label: "User", sortable: false},
  {key: "user_email", label: "Email", sortable: false},
  {key: "request_id", label: "Request ID", sortable: true},
]

const tailFields = [
  {key: "created", label: "Date", thStyle: {maxWidth: "10em"}},
  {key: "name", label: "Event", thStyle: {maxWidth: "20em"}},
  {key: "env", label: "Env", thStyle: {maxWidth: "5em"}},
  {key: "user_name", label: "User"},
  {key: "user_email", label: "Email"},
  {key: "request_id", label: "Request ID"},
]

const csvDownloadUrl = computed(() => {
  const params = new URLSearchParams()
  const nameVal = nameFilter.value.trim()
  params.set("name", nameVal || "report.emailed")
  params.set("format", "csv")
  if (dateFrom.value) params.set("startDate", dateFrom.value)
  if (dateTo.value) params.set("endDate", dateTo.value)
  return `/admin/events/download?${params.toString()}`
})

// ── Watchers ───────────────────────────────────────────────────────────────

watch(tailMode, (enabled: boolean) => {
  if (enabled) startTail()
  else stopTail()
})

// ── Lifecycle ──────────────────────────────────────────────────────────────

const tailInterval = useIntervalFn(pollTail, 5000, {immediate: false})

onBeforeUnmount(() => tailInterval.pause())

// ── Methods ────────────────────────────────────────────────────────────────

const environmentFromNode = (n: string | null) => n?.includes(";") ? n.slice(0, n.indexOf(";")) : (n ?? "unknown")

const envVariant = (e: string) => e === "production" ? "primary" : "secondary"

const onRowClicked = (item: EventEntry) => set(item, "_showDetails", !item._showDetails)
const onTailRowClicked = (item: EventEntry) => set(item, "_showDetails", !item._showDetails)

function toggleTailMode(): void {
  tailMode.value = !tailMode.value
  tailPaused.value = false
}

function toggleTailPause(): void {
  tailPaused.value = !tailPaused.value
}

function scrollToBottom(): void {
  const tailTable = document.querySelector(".tail-table")
  if (tailTable) {
    tailTable.scrollIntoView({behavior: "smooth", block: "end"})
  }
}

function buildTailParams(): URLSearchParams {
  const params = new URLSearchParams()
  if (nameFilter.value.trim()) setQueryParam(params, "name_glob", globParam(nameFilter.value.trim()))
  if (userIdFilter.value.trim()) setQueryParam(params, "user_id_eq", userIdFilter.value.trim())
  if (requestIdFilter.value.trim()) setQueryParam(params, "request_id_glob", globParam(requestIdFilter.value.trim()))
  if (dateFrom.value) setQueryParam(params, "created_ge", dateFrom.value + "T00:00:00")
  if (dateTo.value) setQueryParam(params, "created_le", dateTo.value + "T23:59:59")
  setQueryParam(params, "sortBy", "created")
  setQueryParam(params, "sortDesc", false)
  setQueryParam(params, "pageSize", 100)
  return params
}

async function startTail(): Promise<void> {
  tailRows.value = []
  tailLastCreated.value = null

  try {
    const resp = await fetch(`/service/objects/event_log_entry?${buildTailParams().toString()}`)
    const json = await resp.json()
    const data = json.data || json
    tailRows.value = ((data.data || []) as EventEntry[]).map((item) => ({...item, _showDetails: false}))

    if (tailRows.value.length > 0) {
      tailLastCreated.value = tailRows.value[tailRows.value.length - 1].created
    }
  } catch (err) {
    console.error("Tail initial load error:", err)
  }

  startPolling()
}

function stopTail(): void {
  tailInterval.pause()
}

async function pollTail(): Promise<void> {
  if (document.hidden || tailPaused.value) return

  const params = buildTailParams()
  if (tailLastCreated.value) {
    setQueryParam(params, "created_gt", tailLastCreated.value)
  }

  try {
    const resp = await fetch(`/service/objects/event_log_entry?${params.toString()}`)
    const json = await resp.json()
    const data = json.data || json
    const newRows: EventEntry[] = ((data.data || []) as EventEntry[]).map((item) => ({...item, _showDetails: false}))

    if (newRows.length > 0) {
      tailRows.value.push(...newRows)
      tailLastCreated.value = newRows[newRows.length - 1].created

      if (tailRows.value.length > 500) {
        tailRows.value = tailRows.value.slice(-500)
      }

      if (!tailPaused.value) {
        nextTick(() => scrollToBottom())
      }
    }
  } catch (err) {
    console.error("Tail poll error:", err)
  }
}

function startPolling(): void {
  tailInterval.resume()
}

// ── Test access ────────────────────────────────────────────────────────────

defineExpose({
  nameFilter, userIdFilter, requestIdFilter,
  dateFrom, dateTo, currentPage, perPage, sortBy, sortDesc,
  totalRows, rows, lastPage, doSearch, onSortChanged,
  setDateRange, dateRangeActive,
  tailMode, tailPaused, tailRows,
  fields, tailFields, csvDownloadUrl,
  formatDateTime, environmentFromNode, envVariant,
  onRowClicked, onTailRowClicked,
  toggleTailMode, toggleTailPause,
})
</script>

<template>
  <div>
    <!-- Action bar -->
    <div class="action-bar">
      <b-button
        :variant="tailMode ? 'warning' : 'primary'"
        size="sm"
        @click="toggleTailMode"
      >
        {{ tailMode ? "Stop Tailing" : "Tail Logs" }}
      </b-button>
      <b-button variant="outline-secondary" size="sm" class="ml-2" @click="window.location.href = csvDownloadUrl">
        Download CSV
      </b-button>
    </div>

    <!-- Filter bar -->
    <b-row class="mb-3 align-items-end">
      <b-col cols="12" sm="3" class="mb-2 mb-sm-0">
        <label class="mb-1 small text-muted">Event Name</label>
        <b-form-input
          v-model="nameFilter"
          placeholder="e.g. email.*"
          size="sm"
          @keydown.enter="doSearch()"
        />
      </b-col>
      <b-col cols="6" sm="2" class="mb-2 mb-sm-0">
        <label class="mb-1 small text-muted">User ID</label>
        <b-form-input
          v-model="userIdFilter"
          placeholder="e.g. 123"
          size="sm"
          @keydown.enter="doSearch()"
        />
      </b-col>
      <b-col cols="6" sm="3" class="mb-2 mb-sm-0">
        <label class="mb-1 small text-muted">Request ID</label>
        <b-form-input
          v-model="requestIdFilter"
          placeholder="e.g. req_*"
          size="sm"
          @keydown.enter="doSearch()"
        />
      </b-col>
      <b-col cols="6" sm="2" class="mb-2 mb-sm-0">
        <label class="mb-1 small text-muted">From</label>
        <b-form-input v-model="dateFrom" type="date" size="sm" @keydown.enter="doSearch()" />
      </b-col>
      <b-col cols="6" sm="2" class="mb-2 mb-sm-0">
        <label class="mb-1 small text-muted">To</label>
        <b-form-input v-model="dateTo" type="date" size="sm" @keydown.enter="doSearch()" />
      </b-col>
    </b-row>

    <list-browser-toolbar
      :date-range-active="dateRangeActive"
      @search="doSearch()"
      @set-date-range="setDateRange($event)"
    />

    <!-- Top pagination -->
    <list-browser-pagination
      :current-page="currentPage"
      :total-rows="totalRows"
      :per-page="perPage"
      :last-page="lastPage"
      @page-change="currentPage = $event; doSearch()"
      @per-page-change="perPage = $event; doSearch()"
    />

    <!-- Results -->

    <!-- Results -->
    <b-table
      :items="rows"
      :fields="fields"
      :sort-by="sortBy"
      :sort-desc="sortDesc"
      :current-page="currentPage"
      :per-page="perPage"
      primary-key="id"
      striped
      hover
      responsive
      show-empty
      empty-text="Click Search to load results"
      @row-clicked="onRowClicked"
      @sort-changed="onSortChanged"
    >
      <template #cell(created)="data">
        <span class="date-cell">{{ formatDateTime(data.item.created) }}</span>
      </template>
      <template #cell(name)="data">
        <code>{{ data.item.name }}</code>
      </template>
      <template #cell(env)="data">
        <b-badge :variant="envVariant(environmentFromNode(data.item.node))">
          {{ environmentFromNode(data.item.node) }}
        </b-badge>
      </template>
      <template #cell(user_name)="data">
        <a v-if="data.item.user_id" :href="`/admin/users/edit/${data.item.user_id}`" target="_blank">{{ data.item.user_name || data.item.user_id }}</a>
        <span v-else class="text-muted">—</span>
      </template>
      <template #cell(user_email)="data">
        {{ data.item.user_email || '—' }}
      </template>
      <template #row-details="data">
        <div class="p-3">
          <div class="row mb-2">
            <div class="col-sm-6">
              <dl class="row mb-0">
                <dt class="col-sm-5">Event ID</dt>
                <dd class="col-sm-7"><code>{{ data.item.id }}</code></dd>
                <dt class="col-sm-5">Date</dt>
                <dd class="col-sm-7">{{ formatDateTime(data.item.created) }}</dd>
                <dt class="col-sm-5">Node</dt>
                <dd class="col-sm-7">{{ data.item.node || '—' }}</dd>
              </dl>
            </div>
            <div class="col-sm-6">
              <dl class="row mb-0">
                <dt class="col-sm-5">User</dt>
                <dd class="col-sm-7">{{ data.item.user_name || data.item.user_id || '—' }}</dd>
                <dt class="col-sm-5">Email</dt>
                <dd class="col-sm-7">{{ data.item.user_email || '—' }}</dd>
                <dt class="col-sm-5">Event Name</dt>
                <dd class="col-sm-7"><code>{{ data.item.name }}</code></dd>
              </dl>
            </div>
          </div>
          <hr>
          <expandable-json-detail title="Event Data" :source="data.item" />
        </div>
      </template>
    </b-table>

    <!-- Tail mode: polling table -->
    <div v-if="tailMode">
      <b-table
        class="tail-table"
        :items="tailRows"
        :fields="tailFields"
        small
        striped
        hover
        responsive
        show-empty
        primary-key="id"
        @row-clicked="onTailRowClicked"
      >
        <template #cell(created)="data">
          <span class="date-cell">{{ formatDateTime(data.item.created) }}</span>
        </template>
        <template #cell(name)="data">
          <code>{{ data.item.name }}</code>
        </template>
        <template #cell(env)="data">
          <b-badge :variant="envVariant(environmentFromNode(data.item.node))">
            {{ environmentFromNode(data.item.node) }}
          </b-badge>
        </template>
        <template #cell(user_name)="data">
          <a v-if="data.item.user_id" :href="`/admin/users/edit/${data.item.user_id}`" target="_blank">{{ data.item.user_name || data.item.user_id }}</a>
          <span v-else class="text-muted">—</span>
        </template>
        <template #cell(user_email)="data">
          {{ data.item.user_email || '—' }}
        </template>
        <template #row-details="data">
          <div class="p-3">
            <div class="row mb-2">
              <div class="col-sm-6">
                <dl class="row mb-0">
                  <dt class="col-sm-5">Event ID</dt>
                  <dd class="col-sm-7"><code>{{ data.item.id }}</code></dd>
                  <dt class="col-sm-5">Date</dt>
                  <dd class="col-sm-7">{{ formatDateTime(data.item.created) }}</dd>
                  <dt class="col-sm-5">Node</dt>
                  <dd class="col-sm-7">{{ data.item.node || '—' }}</dd>
                </dl>
              </div>
              <div class="col-sm-6">
                <dl class="row mb-0">
                  <dt class="col-sm-5">User</dt>
                  <dd class="col-sm-7">{{ data.item.user_name || data.item.user_id || '—' }}</dd>
                  <dt class="col-sm-5">Email</dt>
                  <dd class="col-sm-7">{{ data.item.user_email || '—' }}</dd>
                  <dt class="col-sm-5">Event Name</dt>
                  <dd class="col-sm-7"><code>{{ data.item.name }}</code></dd>
                </dl>
              </div>
            </div>
            <hr>
            <expandable-json-detail title="Event Data" :source="data.item" />
          </div>
        </template>
      </b-table>
      <div v-if="tailRows.length > 0" class="tail-controls">
        <b-button size="sm" :variant="tailPaused ? 'success' : 'secondary'" @click="toggleTailPause">
          {{ tailPaused ? 'Resume' : 'Pause' }}
        </b-button>
      </div>
      <div v-if="tailRows.length === 0" class="text-center text-muted py-3">
        Waiting for new events...
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.action-bar {
  display: flex;
  margin-bottom: 1em;
}

.tail-controls {
  display: flex;
  justify-content: center;
  padding: 0.5em 0;
}
</style>
